import { useEffect, useRef } from "react";

import { NAV_ITEMS } from "../data/content";
import "./MobileDrawer.css";

/**
 * Slide-in navigation for tablet and phone.
 *
 * The reference drawer also carries a search field and a cart badge, both
 * inert leftovers from the template it was built on. They are left out here —
 * a control that looks live but does nothing is worse than no control.
 */
export default function MobileDrawer({ open, onClose, active, onNavigate }) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);

  /* Lock the page, move focus in, and keep Tab inside the panel. */
  useEffect(() => {
    if (!open) return undefined;

    document.body.dataset.locked = "true";
    closeRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = panelRef.current?.querySelectorAll(
        'a[href], button:not([disabled])',
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      delete document.body.dataset.locked;
    };
  }, [open, onClose]);

  return (
    <div className="ilab-drawer" data-open={open} aria-hidden={!open}>
      <button
        type="button"
        className="ilab-drawer__scrim"
        onClick={onClose}
        tabIndex={-1}
        aria-hidden="true"
      />

      <div
        id="ilab-drawer"
        className="ilab-drawer__panel"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
      >
        <div className="ilab-drawer__top">
          <img
            className="ilab-drawer__logo"
            src="/assets/logo/logo.png"
            alt="Intelligence Lab"
            width="300"
            height="79"
          />
          <button type="button" className="ilab-drawer__close" onClick={onClose} ref={closeRef}>
            <span className="sr-only">Close menu</span>
            <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20">
              <path
                d="M5 5l14 14M19 5L5 19"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </button>
        </div>

        <nav aria-label="Site">
          <ul className="ilab-drawer__nav">
            {NAV_ITEMS.map((item, i) => (
              <li key={item.href} style={{ "--i": i }}>
                <a
                  href={item.href}
                  onClick={(e) => onNavigate(e, item.href)}
                  aria-current={active === item.href ? "true" : undefined}
                >
                  <span className="ilab-drawer__index">0{i + 1}</span>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <p className="ilab-drawer__foot">Robotics · Programming · App development</p>
      </div>
    </div>
  );
}
